import { consts } from '@allesauto/utils/consts';
import Cookies from 'js-cookie';
import throttle from 'lodash/throttle';
import { useRouter } from 'next/router';
import { FC, useEffect } from 'react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { login, logout } from '@/store/redux/actions/user';

const fiveSeconds = 5000;

export interface LoginToken {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

type AuthContext = {
  accessToken: string;
};

const AuthContext = React.createContext<AuthContext>({
  accessToken: '',
});

const parseLoginCookie = throttle(
  (): LoginToken | null => {
    const cookie = Cookies.get(consts.COOKIES.USER_TOKEN) || '';

    return cookie ? JSON.parse(cookie) : null;
  },
  fiveSeconds,
  { trailing: false }
);

const retrieveAccessToken = (): string => {
  const parsedCookie = parseLoginCookie();

  return parsedCookie ? parsedCookie.accessToken : '';
};

const authCheck = () => {
  return !!Cookies.get(consts.COOKIES.USER_TOKEN);
};

/**
 * Use Context API to store the access token. Protect internal
 * routes to be called without a login cookie
 */
export const AuthProvider: FC<React.PropsWithChildren<any>> = ({
  children,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const loginState = useSelector((state: any) => state.user);
  const [accessToken, setAccessToken] = React.useState<string>(() =>
    retrieveAccessToken()
  );

  /**
   * On route change:
   * - Check whether login cookie is present
   * - Dispatch login() if it is not present in the state (i.e. after a page reload)
   * - Dispatch logout() if redux state and cookie diverge
   */
  useEffect(() => {
    // Case 1: login cookie exists
    if (authCheck()) {
      setAccessToken(retrieveAccessToken);

      if (!loginState.isLoggedIn) {
        const loginCookie = parseLoginCookie();

        if (loginCookie?.expiresAt) {
          dispatch(login(loginCookie.expiresAt));
        }
      }
      // Case 2: login cookie missing
    } else {
      if (loginState.isLoggedIn) {
        dispatch(logout());
      }
      if (accessToken) {
        setAccessToken('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath]);

  return (
    <AuthContext.Provider
      value={{
        accessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContext {
  const context = React.useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth can only be used within AuthProvider');
  }
  return context;
}

export default function useToken(): string {
  const context = useAuth();

  return context.accessToken;
}
